import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { useAuth } from '../contexts/AuthContext';
import { useUserLocation } from '../hooks/useUserLocation';
import { calculateDistance, formatDistance } from '../utils/distance';
import { cleanText } from '../utils/textUtils';
import LocationDisplay from '../components/LocationDisplay';
import FavoriteButton from '../components/FavoriteButton';
import ShareButton from '../components/ShareButton';
import './AttractionsPage.css';
import './SharedCardStyles.css';

interface Attraction { id: string; slug: string; name: string; short_description: string; attraction_type: string; category: string; city: string; address?: string; latitude?: number; longitude?: number; age_min: number; age_max: number; is_free: boolean; adult_price?: number; child_price?: number; price_description?: string; hours_description?: string; image_url?: string; indoor: boolean; outdoor_space: boolean; wheelchair_accessible: boolean; stroller_friendly: boolean; food_available: boolean; parking_available: boolean; membership_available: boolean; rating_avg?: number; rating_count?: number; website?: string; }
interface AgeGroup { label: string; minAge: number; maxAge: number; }
const AGE_GROUPS: AgeGroup[] = [{ label: 'Toddlers', minAge: 0, maxAge: 2 }, { label: '3-6 years', minAge: 3, maxAge: 6 }, { label: '7-10 years', minAge: 7, maxAge: 10 }, { label: 'Teens', minAge: 11, maxAge: 17 }];

const AttractionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { preferences } = useUserPreferences();
  const [selectedCity, setSelectedCity] = useState<string>('Houston');
  const { location: userLocation } = useUserLocation(selectedCity, { requestGeolocation: false });
  const [attractions, setAttractions] = useState<Attraction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedAgeGroups, setSelectedAgeGroups] = useState<AgeGroup[]>([]);

  useEffect(() => { if (preferences?.city) setSelectedCity(preferences.city); else { const savedCity = localStorage.getItem('selectedCity'); if (savedCity) setSelectedCity(savedCity); } }, [preferences]);
  useEffect(() => { fetchAttractions(); if (preferences?.age_ranges && preferences.age_ranges.length > 0) { const userAgeGroups = AGE_GROUPS.filter(group => preferences.age_ranges.includes(group.label.split(' ')[0].toLowerCase())); setSelectedAgeGroups(userAgeGroups); } }, [preferences]);

  const fetchAttractions = async () => {
    try {
      setLoading(true); setError(null);
      let query = supabase.from('attractions').select('*').eq('status', 'active');
      const selectedCity = localStorage.getItem('selectedCity');
      if (selectedCity) query = query.eq('city', selectedCity);
      const { data, error } = await query;
      if (error) throw error;
      // Sort by popularity: rating × reviews (descending)
      const sortedAttractions = (data || []).sort((a, b) => {
        const scoreA = (a.rating_avg || 0) * (a.rating_count || 0);
        const scoreB = (b.rating_avg || 0) * (b.rating_count || 0);
        return scoreB - scoreA; // Highest score first
      });
      setAttractions(sortedAttractions);
    } catch (err: any) { setError(err.message || 'Failed to load attractions'); } finally { setLoading(false); }
  };

  const filteredAttractions = attractions.filter(attr => {
    if (!attr.id || attr.id === 'null' || attr.id === 'undefined') return false;
    if (selectedType !== 'all' && attr.attraction_type !== selectedType) return false;
    if (selectedCategory !== 'all' && attr.category !== selectedCategory) return false;
    if (selectedAgeGroups.length > 0) { const matches = selectedAgeGroups.some(group => attr.age_min <= group.maxAge && attr.age_max >= group.minAge); if (!matches) return false; }
    return true;
  });

  const attractionTypes = Array.from(new Set(attractions.map(a => a.attraction_type)));
  const categories = Array.from(new Set(attractions.map(a => a.category)));
  const getDistanceToAttraction = (attraction: Attraction): string | null => { if (!user || !userLocation || !attraction.latitude || !attraction.longitude) return null; const distance = calculateDistance(userLocation.lat, userLocation.lon, attraction.latitude, attraction.longitude); return formatDistance(distance); };

  if (loading) return <div className="attractions-container"><div className="loading"><div className="spinner"></div><p>Loading attractions...</p></div></div>;
  if (error) return <div className="attractions-container"><div className="error"><p>😕 Oops! {error}</p><button onClick={fetchAttractions} className="retry-btn">Try Again</button></div></div>;

  return (
    <div className="attractions-container">
      <button className="back-button" onClick={() => navigate('/')}>← Back to Home</button>
      <LocationDisplay />
      <div className="attractions-header"><h1>🎢 Attractions</h1><p>Zoos, museums & fun places to visit anytime</p></div>
      <div className="filter-section">
        <div className="filter-group"><h3>Age Group (select multiple)</h3><div className="filter-buttons"><button className={`filter-btn ${selectedAgeGroups.length === 0 ? 'active' : ''}`} onClick={() => setSelectedAgeGroups([])}>All Ages</button>{AGE_GROUPS.map((ageGroup) => <button key={ageGroup.label} className={`filter-btn ${selectedAgeGroups.includes(ageGroup) ? 'active' : ''}`} onClick={() => { if (selectedAgeGroups.includes(ageGroup)) setSelectedAgeGroups(selectedAgeGroups.filter(g => g !== ageGroup)); else setSelectedAgeGroups([...selectedAgeGroups, ageGroup]); }}>{selectedAgeGroups.includes(ageGroup) && '✓ '}{ageGroup.label}</button>)}</div></div>
        <div className="filter-group"><h3>Type</h3><div className="filter-buttons"><button className={`filter-btn ${selectedType === 'all' ? 'active' : ''}`} onClick={() => setSelectedType('all')}>All Types</button>{attractionTypes.map(type => <button key={type} className={`filter-btn ${selectedType === type ? 'active' : ''}`} onClick={() => setSelectedType(type)}>{type}</button>)}</div></div>
        <div className="filter-group"><h3>Category</h3><div className="filter-buttons"><button className={`filter-btn ${selectedCategory === 'all' ? 'active' : ''}`} onClick={() => setSelectedCategory('all')}>All Categories</button>{categories.map(cat => <button key={cat} className={`filter-btn ${selectedCategory === cat ? 'active' : ''}`} onClick={() => setSelectedCategory(cat)}>{cat}</button>)}</div></div>
      </div>
      {filteredAttractions.length === 0 ? <div className="no-attractions"><p>🎢 No attractions found. Check back soon!</p></div> : (
        <div className="attractions-list">
          {filteredAttractions.map(attraction => (
            <div key={attraction.id} className="attraction-card">
              <div className="favorite-button-wrapper"><FavoriteButton itemId={attraction.id} itemType="attraction" size="small" /></div>
              {attraction.image_url && <div className="attraction-image"><img src={attraction.image_url} alt={attraction.name} /></div>}
              <div className="attraction-badges"><span className="badge type-badge">{attraction.attraction_type}</span><span className="badge category-badge">{attraction.category}</span></div>
              <div className="attraction-content">
                <h3 className="attraction-name">{attraction.name}</h3>
                <div className="attraction-location-wrapper">
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      attraction.address 
                        ? `${attraction.name} ${attraction.address}, ${attraction.city}`.trim()
                        : `${attraction.name}, ${attraction.city}`
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="location-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    📍 {attraction.address || attraction.city}
                  </a>
                  {getDistanceToAttraction(attraction) && <span className="attraction-distance">🎯 {getDistanceToAttraction(attraction)}</span>}
                </div>
                <p className="attraction-description">{cleanText(attraction.short_description)}</p>
                <div className="attraction-details">
                  <div className="detail"><span className="detail-icon">👶</span><span>Ages {attraction.age_min}-{attraction.age_max}</span></div>
                  <div className="detail"><span className="detail-icon">💰</span><span>{attraction.is_free ? 'FREE' : attraction.price_description || `$${attraction.child_price || attraction.adult_price}/person`}</span></div>
                  {attraction.hours_description && <div className="detail"><span className="detail-icon">🕐</span><span>{attraction.hours_description}</span></div>}
                  {attraction.rating_avg && attraction.rating_count && <div className="detail"><span className="detail-icon">⭐</span><span>{attraction.rating_avg.toFixed(1)} ({attraction.rating_count} reviews)</span></div>}
                </div>
                <div className="attraction-features">
                  {attraction.indoor && <span className="feature-badge">🏠 Indoor</span>}
                  {attraction.outdoor_space && <span className="feature-badge">🌳 Outdoor</span>}
                  {attraction.wheelchair_accessible && <span className="feature-badge">♿ Accessible</span>}
                  {attraction.stroller_friendly && <span className="feature-badge">🚼 Stroller OK</span>}
                  {attraction.food_available && <span className="feature-badge">🍽️ Food</span>}
                  {attraction.parking_available && <span className="feature-badge">🅿️ Parking</span>}
                  {attraction.membership_available && <span className="feature-badge">💳 Membership</span>}
                </div>
                <div className="attraction-actions">
                  <button 
                    className={`action-button icon-only ${!attraction.website ? 'disabled' : ''}`}
                    onClick={(e) => {
                      if (attraction.website) {
                        e.preventDefault();
                        e.stopPropagation();
                        window.open(attraction.website, '_blank', 'noopener,noreferrer');
                      }
                    }}
                    disabled={!attraction.website}
                    title="Visit Website"
                    aria-label="Visit Website"
                  >
                    🌐
                  </button>
                  <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                    <ShareButton 
                      title={attraction.name}
                      description={attraction.short_description}
                      url={`${window.location.origin}/attractions`}
                      location={`${attraction.name}, ${attraction.city}`}
                      size="small"
                      showLabel={false}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AttractionsPage;
